import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { AuthSrcDirective, UiButtonComponent, UiModels, UiNoDataComponent, UiPreloaderComponent } from '@ui';
import { PersonModel } from '@models/person.model';
import { personFeature } from '@states/person/person.feature';
import { PersonService } from '../../../../../../services/person.service';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-confirm-recognition-dialog',
  imports: [
    AuthSrcDirective,
    TranslatePipe,
    UiButtonComponent,
    UiNoDataComponent,
    UiPreloaderComponent,
    MatTooltip,
  ],
  templateUrl: './confirm-recognition-dialog.component.html',
  styleUrl: './confirm-recognition-dialog.component.scss',
})
export class ConfirmRecognitionDialogComponent implements OnInit {
  private readonly store$ = inject(Store);
  private readonly dialogRef = inject(MatDialogRef<ConfirmRecognitionDialogComponent>);
  private readonly personService = inject(PersonService);

  protected readonly data = inject<PersonModel.RecognitionDialogData>(MAT_DIALOG_DATA);
  protected readonly preloaderColor = UiModels.PreloaderColor;
  protected readonly suggestions = signal<PersonModel.RecognitionDialogSuggestion[]>([]);
  protected readonly isLoadingSuggestions = signal(false);
  protected readonly selectedTrackerIds = signal<Set<string>>(new Set<string>());
  private readonly loadedImageTrackerIds = signal<Set<string>>(new Set<string>());
  private readonly failedImageTrackerIds = signal<Set<string>>(new Set<string>());
  protected readonly positiveSuggestions = computed(() => {
    const selectedTrackerIds = this.selectedTrackerIds();
    const failedIds = this.failedImageTrackerIds();
    return this.suggestions().filter(
      (person) => !selectedTrackerIds.has(person.trackerId) && !failedIds.has(person.trackerId),
    );
  });
  protected readonly selectedSuggestions = computed(() => {
    const selectedTrackerIds = this.selectedTrackerIds();
    return this.suggestions().filter((person) => selectedTrackerIds.has(person.trackerId));
  });
  protected readonly canConfirm = computed(() =>
    !this.isLoadingSuggestions() &&
    this.positiveSuggestions().length > 0,
  );

  public ngOnInit(): void {
    if ('suggestedTrackedPersons' in this.data && this.data.suggestedTrackedPersons) {
      this.suggestions.set(this.data.suggestedTrackedPersons.map((person) => this.mapSuggestedTrackedPerson(person)));
      return;
    }

    if (this.data.personId) {
      this.loadPendingSuggestions(this.data.personId);
      return;
    }

    if (this.data.bucketId) {
      this.loadBucketSuggestions(this.data.bucketId);
    }
  }

  protected closeDialog(): void {
    this.dialogRef.close();
  }

  protected confirmRecognition(): void {
    if (!this.canConfirm()) {
      return;
    }

    const negativeTrackerIds = Array.from(this.selectedTrackerIds());
    const positiveTrackerIds = this.positiveSuggestions().map((person) => person.trackerId);

    this.dialogRef.close(<PersonModel.RecognitionDialogResult> {
      positiveTrackerIds,
      negativeTrackerIds,
    });
  }

  protected selectSuggestion(person: PersonModel.RecognitionDialogSuggestion): void {
    if (this.isSuggestionSelected(person)) return;

    this.selectedTrackerIds.update((selectedTrackerIds) => {
      const nextSelectedTrackerIds = new Set(selectedTrackerIds);
      nextSelectedTrackerIds.add(person.trackerId);
      return nextSelectedTrackerIds;
    });
  }

  protected removeSuggestion(person: PersonModel.RecognitionDialogSuggestion): void {
    this.selectedTrackerIds.update((selectedTrackerIds) => {
      const nextSelectedTrackerIds = new Set(selectedTrackerIds);
      nextSelectedTrackerIds.delete(person.trackerId);
      return nextSelectedTrackerIds;
    });
  }

  protected isSuggestionSelected(person: PersonModel.RecognitionDialogSuggestion): boolean {
    return this.selectedTrackerIds().has(person.trackerId);
  }

  protected onSuggestionImageLoaded(trackerId: string): void {
    this.loadedImageTrackerIds.update((ids) => {
      const next = new Set(ids);
      next.add(trackerId);
      return next;
    });
  }

  protected isSuggestionVisible(trackerId: string): boolean {
    return this.loadedImageTrackerIds().has(trackerId);
  }

  protected onSuggestionImageError(trackerId: string): void {
    this.failedImageTrackerIds.update((ids) => {
      const next = new Set(ids);
      next.add(trackerId);
      return next;
    });
  }

  protected getImagePath(person: PersonModel.RecognitionDialogSuggestion, gcp: boolean = true): string {
    return this.personService.getPersonImagePath(person.bestImage, false, gcp);
  }

  private loadBucketSuggestions(bucketId: number): void {
    const token = this.store$.selectSignal(personFeature.selectUnsavedPersonsToken)();
    if (!token) {
      this.suggestions.set([]);
      return;
    }

    this.isLoadingSuggestions.set(true);
    this.personService
      .getSuggestedTrackedPersons(token, bucketId)
      .pipe(finalize(() => this.isLoadingSuggestions.set(false)))
      .subscribe((persons) => {
        this.suggestions.set(persons.map((person) => this.mapSuggestedTrackedPerson(person)));
      });
  }

  private loadPendingSuggestions(personId: number): void {
    this.isLoadingSuggestions.set(true);
    this.personService
      .getPendingSuggestedTrackers(personId)
      .pipe(finalize(() => this.isLoadingSuggestions.set(false)))
      .subscribe((response) => {
        this.suggestions.set(response.pending.map((person) => this.mapPendingTracker(person)));
      });
  }

  private mapSuggestedTrackedPerson(
    person: PersonModel.SuggestedTrackedPerson,
  ): PersonModel.RecognitionDialogSuggestion {
    return {
      trackerId: person.trackerId,
      bestImage: person.faceImage,
    };
  }

  private mapPendingTracker(
    person: PersonModel.API.PendingTrainTrackerResponse,
  ): PersonModel.RecognitionDialogSuggestion {
    return {
      trackerId: person.trackerId,
      bestImage: person.zoomImage ?? person.bestImage ?? '',
    };
  }
}
